import type { Table } from "@tanstack/react-table";
import { useEffect, useMemo } from "react";

import type { EmployeeListItem } from "~/components/employees/employee-list-table-columns";
import { Button } from "~/components/ui/button";
import { DataTableFacetedFilter } from "~/components/ui/data-table";
import { Input } from "~/components/ui/input";

export function EmployeeTableToolbar({
  table,
}: {
  table: Table<EmployeeListItem>;
}) {
  const isFiltered = table.getState().columnFilters.length > 0;
  const flatRows = table.getCoreRowModel().flatRows;

  const roleOptions = useMemo(
    () =>
      flatRows
        .flatMap((row) =>
          row.original.roles.filter(Boolean).map((role) => ({
            label: role?.name ?? "",
            value: role?._id ?? "",
          }))
        )
        .filter(
          (role, index, self) =>
            self.findIndex((r) => r.value === role.value) === index
        ),
    [flatRows]
  );

  const locationOptions = useMemo(
    () =>
      flatRows
        .flatMap((row) =>
          row.original.locations.data.filter(Boolean).map((location) => ({
            label: location?.name ?? "",
            value: location?._id ?? "",
          }))
        )
        .filter(
          (location, index, self) =>
            self.findIndex((r) => r.value === location.value) === index
        ),
    [flatRows]
  );

  useEffect(() => {
    table.getColumn("status")?.setFilterValue(["Enabled"]);
  }, [table]);

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center space-x-2" id="table-toolbar">
        <Input
          className="body-sm w-48 lg:w-60"
          onChange={(event) =>
            table.getColumn("name")?.setFilterValue(event.target.value)
          }
          placeholder="Search name..."
          value={(() => {
            const filterValue = table.getColumn("name")?.getFilterValue();
            return typeof filterValue === "string" ? filterValue : "";
          })()}
        />
        <DataTableFacetedFilter
          column={table.getColumn("roles")}
          options={roleOptions}
          placeholder="Role..."
          title="Roles"
        />
        <DataTableFacetedFilter
          column={table.getColumn("locations")}
          options={locationOptions}
          placeholder="Location name..."
          title="Locations"
        />
        <DataTableFacetedFilter
          column={table.getColumn("status")}
          options={[
            {
              label: "Enabled",
              value: "Enabled",
            },
            {
              label: "Disabled",
              value: "Disabled",
            },
          ]}
          placeholder="Enabled or disabled..."
          title="Status"
        />

        {isFiltered && (
          <Button
            className="h-8 px-2 lg:px-3"
            onClick={() => table.resetColumnFilters()}
            type="button"
            variant="ghost"
          >
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}
